import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "./supabase";
import { useTenant } from "./tenantContext";

// Phase 4.5 — Admin Panel MVP
//
// Tenant CRUD for platform admins. Gated by the `is_admin` claim
// injected into the JWT by the Phase 2.2 custom_access_token_hook.
// Reads tenants via useTenant() (which already fetches all tenants
// for admins) and writes directly to the tenants table via Supabase
// client. RLS policies tenants_admin_insert/update/delete enforce
// that only platform admins can mutate.
//
// What's intentionally NOT here (Phase 4.5b / later):
//   • Live branding preview pane
//   • Audit log viewer
//   • Stripe customer creation (Phase 8)
//   • parent_tenant_id field for franchise hierarchy (defer)


function isValidSlug(s) {
  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(s);
}

function initFormState(tenant) {
  const b = tenant?.branding || {};
  return {
    slug: tenant?.slug || "",
    name: tenant?.name || "",
    shortName: b.shortName || "",
    logo: b.logo || "",
    sidebarLabel: b.sidebarLabel || "Sales CRM",
    footerText: b.footerText || "",
    industries: (b.industries || []).join(", "),
    defaultIndustry: b.defaultIndustry || "",
    defaultSubIndustry: b.defaultSubIndustry || "",
    primaryColor: b.primaryColor || "#2563EB",
    themeColor: b.themeColor || "#1a2744",
    sidebarGradient: b.sidebarGradient || "linear-gradient(180deg,#1a2744 0%,#243454 100%)",
    fromName: b.fromName || "",
    fromEmail: b.fromEmail || "",
    companyTitle: b.companyTitle || "",
    website: b.website || "",
    phone: b.phone || "",
    signatureLogoUrl: b.signatureLogoUrl || "",
  };
}

function buildBranding(f, existing) {
  // Preserve pitchKeys (managed externally for now) and any other
  // branding fields the form doesn't expose
  const preserved = existing?.branding || {};
  return {
    ...preserved,
    name: f.name,
    shortName: f.shortName,
    logo: f.logo,
    sidebarLabel: f.sidebarLabel,
    footerText: f.footerText,
    industries: f.industries.split(",").map(s => s.trim()).filter(Boolean),
    defaultIndustry: f.defaultIndustry,
    defaultSubIndustry: f.defaultSubIndustry,
    primaryColor: f.primaryColor,
    themeColor: f.themeColor,
    sidebarGradient: f.sidebarGradient,
    fromName: f.fromName,
    fromEmail: f.fromEmail,
    companyTitle: f.companyTitle,
    website: f.website,
    phone: f.phone,
    signatureLogoUrl: f.signatureLogoUrl,
    pitchKeys: preserved.pitchKeys || [],
  };
}

export default function AdminPanel({ onExit, userEmail }) {
  const { tenants, isAdmin: isPlatformAdmin, refresh } = useTenant();
  const [view, setView] = useState("list");
  const [editingId, setEditingId] = useState(null);

  if (!isPlatformAdmin) {
    return (
      <AdminShell onExit={onExit} userEmail={userEmail}>
        <div style={{padding: "80px 40px", textAlign: "center"}}>
          <div style={{fontSize: 22, fontWeight: 700, color: "#1B3A5C", marginBottom: 8}}>Access denied</div>
          <div style={{color: "#64748B", marginBottom: 24}}>The admin panel requires platform admin privileges.</div>
          <button className="btn btn-ghost" onClick={onExit}>← Back to workspaces</button>
        </div>
      </AdminShell>
    );
  }

  const handleAfterChange = async () => {
    await refresh();
    setView("list");
    setEditingId(null);
  };

  let content;
  if (view === "create") {
    content = <TenantForm onSaved={handleAfterChange} onCancel={() => setView("list")} />;
  } else if (view === "edit") {
    const t = tenants.find(t => t.id === editingId);
    if (!t) {
      setView("list");
      content = null;
    } else {
      content = <TenantForm tenant={t} onSaved={handleAfterChange} onCancel={() => setView("list")} />;
    }
  } else {
    content = (
      <TenantList
        tenants={tenants}
        onCreate={() => setView("create")}
        onEdit={(id) => { setEditingId(id); setView("edit"); }}
      />
    );
  }

  return (
    <AdminShell onExit={onExit} userEmail={userEmail}>
      {content}
    </AdminShell>
  );
}

const ADMIN_CSS = `
.admin-shell input[type="text"],
.admin-shell input[type="email"],
.admin-shell input[type="url"],
.admin-shell input:not([type]),
.admin-shell select,
.admin-shell textarea {
  width: 100%;
  padding: 14px 16px;
  font-size: 15px;
  line-height: 1.4;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  outline: none;
  background: #fff;
  color: #334155;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color .15s, box-shadow .15s;
}
.admin-shell input:focus,
.admin-shell select:focus,
.admin-shell textarea:focus {
  border-color: #2563EB;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}
.admin-shell input:disabled,
.admin-shell select:disabled,
.admin-shell textarea:disabled {
  background: #F1F5F9;
  color: #94A3B8;
  cursor: not-allowed;
}
.admin-shell textarea {
  min-height: 88px;
  resize: vertical;
  font-family: inherit;
}
.admin-shell select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 14px;
  padding-right: 42px;
}
.admin-shell input[type="color"] {
  padding: 0 !important;
  width: 56px !important;
  height: 50px !important;
  cursor: pointer;
}
`;

function AdminShell({ onExit, userEmail, children }) {
  return (
    <div className="admin-shell" style={{minHeight: "100vh", background: "#F8FAFC", display: "flex", flexDirection: "column"}}>
      <style>{ADMIN_CSS}</style>
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #E2E8F0",
        padding: "20px 40px",
        display: "flex",
        alignItems: "center",
        gap: 20,
      }}>
        <img src="/vd-icon-solo.png" alt="Vector Digital" style={{height: 52, width: 52, objectFit: "contain"}}/>
        <div style={{flex: 1}}>
          <div style={{fontSize: 12, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700}}>
            Platform Admin
          </div>
          <div style={{fontSize: 22, fontWeight: 800, color: "#1B3A5C", lineHeight: 1.2}}>Vector Digital CRM</div>
        </div>
        {userEmail && (
          <div style={{fontSize: 13, color: "#64748B", textAlign: "right"}}>
            <div style={{fontWeight: 700, color: "#475569"}}>{userEmail}</div>
            <div style={{fontSize: 11, marginTop: 2}}>platform admin</div>
          </div>
        )}
        <button className="btn btn-ghost" onClick={onExit} style={{fontSize: 14, padding: "10px 18px"}}>← Workspaces</button>
      </div>
      <div style={{flex: 1, padding: "40px 0"}}>
        {children}
      </div>
    </div>
  );
}

function TenantList({ tenants, onCreate, onEdit }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(t =>
      (t.name || "").toLowerCase().includes(q) ||
      (t.slug || "").toLowerCase().includes(q)
    );
  }, [tenants, search]);

  return (
    <div style={{padding: "0 40px", maxWidth: 1280, margin: "0 auto"}}>
      <div style={{display: "flex", alignItems: "center", gap: 20, marginBottom: 32}}>
        <div style={{flex: 1}}>
          <div style={{fontSize: 14, color: "#64748B", marginBottom: 4, fontWeight: 600}}>Manage tenants</div>
          <div style={{fontSize: 32, fontWeight: 800, color: "#1B3A5C", lineHeight: 1.1}}>Tenants</div>
        </div>
        <button className="btn btn-primary" onClick={onCreate} style={{fontSize: 15, padding: "12px 22px", fontWeight: 700}}>
          + New Tenant
        </button>
      </div>

      <input
        type="text"
        placeholder="Search tenants by name or slug..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: "100%",
          padding: "16px 20px",
          borderRadius: 12,
          border: "1.5px solid #E2E8F0",
          fontSize: 15,
          marginBottom: 20,
          outline: "none",
          boxSizing: "border-box",
        }}
      />

      <div style={{background: "#fff", borderRadius: 14, border: "1px solid #E2E8F0", overflow: "hidden"}}>
        {filtered.length === 0 ? (
          <div style={{padding: 64, textAlign: "center", color: "#94A3B8", fontSize: 15}}>
            {search ? <>No tenants match "<strong>{search}</strong>"</> : "No tenants yet — click \"+ New Tenant\" to create one."}
          </div>
        ) : (
          filtered.map((t, i) => (
            <div
              key={t.id}
              onClick={() => onEdit(t.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 20,
                padding: "24px 28px",
                borderBottom: i < filtered.length - 1 ? "1px solid #F1F5F9" : "none",
                cursor: "pointer",
                transition: "background .15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
              onMouseLeave={e => e.currentTarget.style.background = ""}
            >
              <img
                src={t.branding?.logo || "/vd-logo.png"}
                alt={t.name}
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 12,
                  objectFit: "contain",
                  background: "#fff",
                  padding: 6,
                  border: "1px solid #F1F5F9",
                  flexShrink: 0,
                }}
              />
              <div style={{flex: 1, minWidth: 0}}>
                <div style={{fontWeight: 700, color: "#1B3A5C", fontSize: 19, marginBottom: 6}}>{t.name}</div>
                <div style={{fontSize: 13, color: "#64748B", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap"}}>
                  <span style={{fontFamily: "monospace", background: "#F1F5F9", padding: "2px 8px", borderRadius: 4}}>{t.slug}</span>
                  <span style={{textTransform: "capitalize"}}>{t.plan}</span>
                  <StatusBadge status={t.status} />
                </div>
              </div>
              <div style={{color: "#94A3B8", fontSize: 26, paddingRight: 8}}>›</div>
            </div>
          ))
        )}
      </div>

      <div style={{marginTop: 16, fontSize: 13, color: "#94A3B8", textAlign: "center"}}>
        {filtered.length} of {tenants.length} tenant{tenants.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    active:    { bg: "#DCFCE7", fg: "#15803D" },
    suspended: { bg: "#FEF3C7", fg: "#92400E" },
    cancelled: { bg: "#FEE2E2", fg: "#991B1B" },
  };
  const c = colors[status] || { bg: "#E2E8F0", fg: "#475569" };
  return (
    <span style={{
      background: c.bg,
      color: c.fg,
      padding: "2px 8px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      textTransform: "capitalize",
    }}>{status}</span>
  );
}

function TenantForm({ tenant, onSaved, onCancel }) {
  const isEdit = !!tenant;
  const [form, setForm] = useState(() => initFormState(tenant));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setField = (k, v) => setForm(p => ({...p, [k]: v}));

  const save = async () => {
    setError(null);

    if (!form.name.trim()) return setError("Name is required");
    if (!form.slug.trim()) return setError("Slug is required");
    if (!isValidSlug(form.slug)) {
      return setError("Slug must be lowercase letters/numbers/dashes (e.g. 'acme-corp')");
    }

    setSaving(true);
    try {
      const branding = buildBranding(form, tenant);
      if (isEdit) {
        const { error } = await supabase
          .from("tenants")
          .update({ name: form.name, branding })
          .eq("id", tenant.id);
        if (error) throw error;
      } else {
        const { data: newTenant, error } = await supabase
          .from("tenants")
          .insert({ slug: form.slug, name: form.name, branding })
          .select()
          .single();
        if (error) throw error;

        // Auto-add the creator as an admin member of the new tenant.
        // Without this row, the workspace switcher's update on
        // tenant_users.is_default affects 0 rows and the user stays
        // in their previous workspace — orphaned tenant.
        const { data: { user } } = await supabase.auth.getUser();
        if (user && newTenant) {
          const { error: memErr } = await supabase
            .from("tenant_users")
            .insert({ user_id: user.id, tenant_id: newTenant.id, role: "admin", is_default: false });
          if (memErr) throw memErr;
        }
      }
      await onSaved();
    } catch (e) {
      setError(e.message || String(e));
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    const ok = window.confirm(
      `Permanently delete tenant "${tenant.name}"?\n\n` +
      `This will cascade-delete all leads, email templates, drip sequences, ` +
      `outreach log entries, and user assignments for this tenant.\n\n` +
      `This cannot be undone.`
    );
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      const { error } = await supabase.from("tenants").delete().eq("id", tenant.id);
      if (error) throw error;
      await onSaved();
    } catch (e) {
      setError(e.message || String(e));
      setSaving(false);
    }
  };

  return (
    <div style={{padding: "0 40px", maxWidth: 1000, margin: "0 auto"}}>
      <div style={{display: "flex", alignItems: "center", gap: 20, marginBottom: 32}}>
        <button className="btn btn-ghost" onClick={onCancel} style={{fontSize: 14, padding: "10px 18px"}}>← Cancel</button>
        <div style={{flex: 1}}>
          <div style={{fontSize: 14, color: "#64748B", marginBottom: 4, fontWeight: 600}}>
            {isEdit ? "Edit tenant" : "New tenant"}
          </div>
          <div style={{fontSize: 32, fontWeight: 800, color: "#1B3A5C", lineHeight: 1.1}}>
            {isEdit ? tenant.name : "Create a new tenant"}
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          background: "#FEE2E2",
          color: "#991B1B",
          padding: "12px 16px",
          borderRadius: 8,
          marginBottom: 20,
          fontSize: 14,
          border: "1px solid #FCA5A5",
        }}>
          {error}
        </div>
      )}

      <FormSection title="General">
        <FormRow label="Name" required>
          <input type="text" value={form.name} onChange={e => setField("name", e.target.value)} />
        </FormRow>
        <FormRow
          label="Slug"
          required
          hint={isEdit ? "Cannot be changed after creation" : "lowercase, dashes, used in subdomain URLs (e.g. acme.crm.vectordigital.io)"}
        >
          <input
            type="text"
            value={form.slug}
            onChange={e => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
            disabled={isEdit}
            placeholder="acme-corp"
            style={isEdit ? {background: "#F8FAFC", color: "#94A3B8"} : {}}
          />
        </FormRow>
      </FormSection>

      <FormSection title="Branding">
        <FormRow label="Short Name" hint="Compact label, e.g. 'VD' or 'MGIT'">
          <input type="text" value={form.shortName} onChange={e => setField("shortName", e.target.value)} />
        </FormRow>
        <FormRow label="Logo" hint="Upload an image file, or paste a URL. Shown in the sidebar and tenant list.">
          <LogoUpload
            value={form.logo}
            onChange={v => setField("logo", v)}
            tenantKey={form.slug || form.name}
            idSuffix="primary"
          />
        </FormRow>
        <FormRow label="Sidebar Label" hint="Text shown under the logo in the sidebar">
          <input type="text" value={form.sidebarLabel} onChange={e => setField("sidebarLabel", e.target.value)} />
        </FormRow>
        <FormRow label="Footer Text">
          <textarea rows="2" value={form.footerText} onChange={e => setField("footerText", e.target.value)} />
        </FormRow>
        <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14}}>
          <FormRow label="Primary Color">
            <ColorInput value={form.primaryColor} onChange={v => setField("primaryColor", v)} />
          </FormRow>
          <FormRow label="Theme Color" hint="Sidebar background">
            <ColorInput value={form.themeColor} onChange={v => setField("themeColor", v)} />
          </FormRow>
        </div>
        <FormRow label="Sidebar Gradient (CSS)" hint="Advanced — full CSS gradient string">
          <input type="text" value={form.sidebarGradient} onChange={e => setField("sidebarGradient", e.target.value)} />
        </FormRow>
        <FormRow label="Signature Logo" hint="Optional — separate logo for email signatures">
          <LogoUpload
            value={form.signatureLogoUrl}
            onChange={v => setField("signatureLogoUrl", v)}
            tenantKey={form.slug || form.name}
            idSuffix="signature"
          />
        </FormRow>
      </FormSection>

      <FormSection title="Sales Configuration">
        <FormRow label="Industries" hint="Comma-separated list — what this tenant prospects against">
          <textarea
            rows="2"
            value={form.industries}
            onChange={e => setField("industries", e.target.value)}
            placeholder="Financial, Legal, Healthcare"
          />
        </FormRow>
        <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14}}>
          <FormRow label="Default Industry" hint="Pre-selected on new leads">
            <input type="text" value={form.defaultIndustry} onChange={e => setField("defaultIndustry", e.target.value)} />
          </FormRow>
          <FormRow label="Default Sub-Industry">
            <input type="text" value={form.defaultSubIndustry} onChange={e => setField("defaultSubIndustry", e.target.value)} />
          </FormRow>
        </div>
      </FormSection>

      <FormSection title="Email & Contact">
        <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14}}>
          <FormRow label="From Name" hint="Sender name for drip emails">
            <input type="text" value={form.fromName} onChange={e => setField("fromName", e.target.value)} />
          </FormRow>
          <FormRow label="From Email">
            <input type="email" value={form.fromEmail} onChange={e => setField("fromEmail", e.target.value)} />
          </FormRow>
        </div>
        <FormRow label="Company Title" hint="Legal name shown in email signatures">
          <input type="text" value={form.companyTitle} onChange={e => setField("companyTitle", e.target.value)} />
        </FormRow>
        <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14}}>
          <FormRow label="Website">
            <input type="text" value={form.website} onChange={e => setField("website", e.target.value)} />
          </FormRow>
          <FormRow label="Phone">
            <input type="text" value={form.phone} onChange={e => setField("phone", e.target.value)} />
          </FormRow>
        </div>
      </FormSection>

      {isEdit && <TenantUsersSection tenantId={tenant.id} tenantName={tenant.name} />}

      <div style={{
        display: "flex",
        gap: 12,
        justifyContent: "space-between",
        paddingTop: 16,
        borderTop: "1px solid #E2E8F0",
        marginTop: 24,
      }}>
        {isEdit ? (
          <button
            className="btn"
            style={{background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5"}}
            onClick={remove}
            disabled={saving}
          >
            Delete tenant
          </button>
        ) : <div/>}
        <div style={{display: "flex", gap: 12}}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving..." : (isEdit ? "Save changes" : "Create tenant")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      border: "1px solid #E2E8F0",
      padding: 28,
      marginBottom: 20,
    }}>
      <div style={{
        fontWeight: 700,
        color: "#1B3A5C",
        fontSize: 13,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: 22,
        paddingBottom: 14,
        borderBottom: "1px solid #F1F5F9",
      }}>{title}</div>
      <div style={{display: "flex", flexDirection: "column", gap: 18}}>{children}</div>
    </div>
  );
}

function FormRow({ label, hint, required, children }) {
  return (
    <div>
      <label style={{display: "block", fontSize: 14, color: "#475569", fontWeight: 600, marginBottom: 8}}>
        {label}{required && <span style={{color: "#DC2626", marginLeft: 4}}>*</span>}
      </label>
      {children}
      {hint && <div style={{fontSize: 12, color: "#94A3B8", marginTop: 6, lineHeight: 1.4}}>{hint}</div>}
    </div>
  );
}

function ColorInput({ value, onChange }) {
  return (
    <div style={{display: "flex", gap: 10, alignItems: "stretch"}}>
      <input
        type="color"
        value={value || "#000000"}
        onChange={e => onChange(e.target.value)}
      />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{flex: 1}}
      />
    </div>
  );
}

const LOGO_BUCKET = "tenant-logos";
const LOGO_MAX_BYTES = 5 * 1024 * 1024; // 5 MB — matches the bucket config
const LOGO_ACCEPT = "image/png,image/jpeg,image/gif,image/webp,image/svg+xml";

function slugifyForPath(s) {
  return (s || "tenant")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "tenant";
}

// Upload widget for branding logos. Shows a live preview, an upload
// button (which opens the native file picker), drag-and-drop, progress
// state, and a remove button. Stores the resulting public URL via
// onChange. The raw URL stays editable under "Advanced" for power users
// who still want to paste a path (e.g. /vd-logo.png from the public dir).
function LogoUpload({ value, onChange, tenantKey, idSuffix = "" }) {
  const inputId = `logo-file-${idSuffix || tenantKey || "new"}`;
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    setError(null);

    if (!file.type.startsWith("image/")) {
      return setError("Please choose an image file (PNG, JPG, GIF, WEBP, or SVG).");
    }
    if (file.size > LOGO_MAX_BYTES) {
      return setError(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 5 MB.`);
    }

    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "") || "png";
      const folder = slugifyForPath(tenantKey);
      const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from(LOGO_BUCKET)
        .upload(path, file, {
          cacheControl: "3600",
          upsert: true,
          contentType: file.type,
        });
      if (upErr) throw upErr;

      const { data } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path);
      if (!data?.publicUrl) throw new Error("Upload succeeded but no public URL was returned.");
      onChange(data.publicUrl);
    } catch (e) {
      const msg = e.message || String(e);
      setError(
        /bucket not found/i.test(msg)
          ? "Storage bucket 'tenant-logos' not found. Run the latest DB migrations."
          : msg
      );
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    doUpload(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    doUpload(e.dataTransfer.files?.[0]);
  };

  return (
    <div>
      <input
        id={inputId}
        type="file"
        accept={LOGO_ACCEPT}
        onChange={onPick}
        style={{display: "none"}}
      />

      <div
        onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: 16,
          border: `1.5px dashed ${dragOver ? "#2563EB" : "#CBD5E1"}`,
          background: dragOver ? "#EFF6FF" : "#F8FAFC",
          borderRadius: 12,
          transition: "border-color .15s, background .15s",
        }}
      >
        {/* Preview */}
        <div style={{
          width: 72,
          height: 72,
          borderRadius: 12,
          background: "#fff",
          border: "1px solid #E2E8F0",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}>
          {value ? (
            <img
              src={value}
              alt="Logo preview"
              style={{maxWidth: "100%", maxHeight: "100%", objectFit: "contain", padding: 6}}
              onError={e => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <span style={{fontSize: 24, color: "#CBD5E1"}}>🖼</span>
          )}
        </div>

        {/* Actions */}
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center"}}>
            <label
              htmlFor={inputId}
              className="btn btn-primary"
              style={{
                cursor: uploading ? "default" : "pointer",
                opacity: uploading ? 0.7 : 1,
                fontSize: 14,
                padding: "10px 16px",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {uploading
                ? "Uploading…"
                : value ? "Replace logo" : "Upload logo"}
            </label>
            {value && !uploading && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { onChange(""); setError(null); }}
                style={{fontSize: 13, padding: "10px 14px", color: "#991B1B"}}
              >
                Remove
              </button>
            )}
          </div>
          <div style={{fontSize: 12, color: "#94A3B8", marginTop: 8, lineHeight: 1.4}}>
            {uploading
              ? "Uploading to storage…"
              : "Drag an image here or click to browse. PNG, JPG, GIF, WEBP, or SVG — up to 5 MB."}
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          background: "#FEE2E2",
          color: "#991B1B",
          padding: "8px 12px",
          borderRadius: 8,
          marginTop: 10,
          fontSize: 13,
          border: "1px solid #FCA5A5",
        }}>
          {error}
        </div>
      )}

      {/* Advanced: paste/edit the raw URL */}
      <button
        type="button"
        onClick={() => setShowUrl(v => !v)}
        style={{
          background: "none",
          border: "none",
          color: "#64748B",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          padding: "8px 0 0",
        }}
      >
        {showUrl ? "▾ Hide URL" : "▸ Or paste an image URL"}
      </button>
      {showUrl && (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="/custom-logo.png or https://…"
          style={{marginTop: 8}}
        />
      )}
    </div>
  );
}


function timeAgo(date) {
  if (!date) return "Never signed in";
  const d = new Date(date);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

/* ─── User management section (inside tenant edit form) ──── */

function TenantUsersSection({ tenantId, tenantName }) {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, pendingRes] = await Promise.all([
        supabase.rpc("admin_get_tenant_users", { p_tenant_id: tenantId }),
        supabase.rpc("admin_get_tenant_pending_invitations", { p_tenant_id: tenantId }),
      ]);
      if (usersRes.error) throw usersRes.error;
      if (pendingRes.error) throw pendingRes.error;
      setUsers(usersRes.data || []);
      setPending(pendingRes.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleCancelInvite = async (email) => {
    if (!window.confirm(`Cancel invitation for ${email}?\n\nThey'll no longer be assigned to this tenant when they sign in.`)) return;
    try {
      const { error } = await supabase.rpc("admin_cancel_invitation", {
        p_email: email,
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      await load();
    } catch (e) {
      alert("Failed to cancel invitation: " + e.message);
    }
  };

  const handleRemove = async (u) => {
    if (!window.confirm(`Remove ${u.email} from ${tenantName}?\n\nThey'll lose access to this workspace immediately on their next sign-in.`)) return;
    try {
      const { error } = await supabase
        .from("tenant_users")
        .delete()
        .eq("user_id", u.user_id)
        .eq("tenant_id", tenantId);
      if (error) throw error;
      await load();
    } catch (e) {
      alert("Failed to remove user: " + e.message);
    }
  };

  const handleSetDefault = async (u) => {
    try {
      const { error } = await supabase.rpc("admin_set_user_default_tenant", {
        p_user_id:   u.user_id,
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      await load();
    } catch (e) {
      alert("Failed to set default tenant: " + e.message);
    }
  };

  const handleChangeRole = async (u, newRole) => {
    try {
      const { error } = await supabase
        .from("tenant_users")
        .update({ role: newRole })
        .eq("user_id", u.user_id)
        .eq("tenant_id", tenantId);
      if (error) throw error;
      await load();
    } catch (e) {
      alert("Failed to change role: " + e.message);
    }
  };

  return (
    <>
      <FormSection title={`Users (${users.length}${pending.length > 0 ? ` + ${pending.length} pending` : ""})`}>
        <div style={{fontSize: 13, color: "#64748B", marginBottom: 4}}>
          People with access to this workspace. Existing Supabase users can be added directly; brand-new email addresses are saved as pending invitations and auto-fulfilled when the user first signs in via Authentik.
        </div>

        {error && (
          <div style={{background: "#FEE2E2", color: "#991B1B", padding: 10, borderRadius: 8, fontSize: 13}}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{color: "#94A3B8", fontSize: 14, padding: "8px 0"}}>Loading users...</div>
        ) : users.length === 0 ? (
          <div style={{
            background: "#F8FAFC",
            border: "1px dashed #E2E8F0",
            borderRadius: 10,
            padding: 24,
            textAlign: "center",
            color: "#64748B",
            fontSize: 14,
          }}>
            No users assigned yet. Click <strong>+ Add User</strong> to assign someone.
          </div>
        ) : (
          <div style={{display: "flex", flexDirection: "column", gap: 10}}>
            {users.map(u => (
              <div key={u.user_id} style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "14px 18px",
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: 10,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: "#1B3A5C", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 14, flexShrink: 0,
                }}>
                  {(u.display_name || u.email || "?").charAt(0).toUpperCase()}
                </div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={{fontWeight: 700, color: "#1B3A5C", fontSize: 15}}>
                    {u.display_name}
                    {u.is_default && (
                      <span style={{
                        marginLeft: 8,
                        fontSize: 11,
                        background: "#DBEAFE",
                        color: "#1E40AF",
                        padding: "2px 8px",
                        borderRadius: 6,
                        fontWeight: 700,
                      }}>DEFAULT</span>
                    )}
                  </div>
                  <div style={{fontSize: 13, color: "#64748B", marginTop: 2}}>{u.email}</div>
                  <div style={{fontSize: 11, color: "#94A3B8", marginTop: 2}}>
                    Last sign-in: {timeAgo(u.last_sign_in_at)}
                  </div>
                </div>
                <select
                  value={u.role}
                  onChange={e => handleChangeRole(u, e.target.value)}
                  style={{width: 130, padding: "8px 12px", fontSize: 13}}
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </select>
                {!u.is_default && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleSetDefault(u)}
                    title="Set as this user's default workspace"
                    style={{fontSize: 12}}
                  >
                    Set default
                  </button>
                )}
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleRemove(u)}
                  style={{fontSize: 12, color: "#991B1B"}}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {pending.length > 0 && (
          <div style={{marginTop: 8}}>
            <div style={{fontSize: 12, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10}}>
              Pending invitations
            </div>
            <div style={{display: "flex", flexDirection: "column", gap: 8}}>
              {pending.map(p => (
                <div key={p.email} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "12px 16px",
                  background: "#FFFBEB",
                  border: "1px solid #FCD34D",
                  borderRadius: 10,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: "#F59E0B", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: 13, flexShrink: 0,
                  }}>
                    ⏳
                  </div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontWeight: 700, color: "#1B3A5C", fontSize: 14}}>{p.email}</div>
                    <div style={{fontSize: 12, color: "#92400E", marginTop: 2}}>
                      Invited as <strong>{p.role}</strong> {timeAgo(p.invited_at)}
                      {p.invited_by_email && <> by {p.invited_by_email}</>}
                    </div>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleCancelInvite(p.email)}
                    style={{fontSize: 12, color: "#991B1B"}}
                  >
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{display: "flex", justifyContent: "flex-end", marginTop: 4}}>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            + Add or invite user
          </button>
        </div>
      </FormSection>

      {showAdd && (
        <AddUserModal
          tenantId={tenantId}
          tenantName={tenantName}
          existingUserIds={users.map(u => u.user_id)}
          pendingEmails={pending.map(p => p.email.toLowerCase())}
          onClose={() => setShowAdd(false)}
          onAdded={async () => { setShowAdd(false); await load(); }}
        />
      )}
    </>
  );
}

function AddUserModal({ tenantId, tenantName, existingUserIds, pendingEmails = [], onClose, onAdded }) {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [inviteEmail, setInviteEmail] = useState(null); // when set, we're in "invite by email" mode
  const [role, setRole] = useState("member");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc("admin_list_users");
        if (error) throw error;
        setAllUsers(data || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const candidates = useMemo(() => {
    const existing = new Set(existingUserIds);
    const q = search.trim().toLowerCase();
    return allUsers
      .filter(u => !existing.has(u.id))
      .filter(u => !q || (u.email || "").toLowerCase().includes(q) || (u.display_name || "").toLowerCase().includes(q));
  }, [allUsers, existingUserIds, search]);

  const selectedUser = allUsers.find(u => u.id === selectedUserId);

  const trimmedSearch = search.trim();
  const looksLikeEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedSearch);
  const exactEmailMatch = allUsers.find(u => (u.email || "").toLowerCase() === trimmedSearch.toLowerCase());
  const alreadyPending = pendingEmails.includes(trimmedSearch.toLowerCase());
  const canInvite = looksLikeEmail && !exactEmailMatch && !alreadyPending;

  const handleSelect = (user) => {
    setSelectedUserId(user.id);
    setInviteEmail(null);
  };

  const handleInviteEmailMode = () => {
    setSelectedUserId(null);
    setInviteEmail(trimmedSearch);
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    setWarning(null);
    setSuccessMessage(null);
    try {
      if (selectedUserId) {
        // Existing user — direct add via tenant_users insert
        const { error } = await supabase
          .from("tenant_users")
          .insert({ user_id: selectedUserId, tenant_id: tenantId, role, is_default: false });
        if (error) throw error;
        await onAdded();
      } else if (inviteEmail) {
        // New email — call the invite-tenant-user Edge Function which
        // (a) records the pending invitation in the DB, and
        // (b) creates the user in Authentik + sends an invitation email
        //     (if the AUTHENTIK_* secrets are configured)
        const { data, error } = await supabase.functions.invoke("invite-tenant-user", {
          body: { email: inviteEmail, tenant_id: tenantId, role },
        });
        if (error) throw error;

        // The function returns 200 with a `warning` field when the
        // pending row was created but the Authentik flow failed.
        // Keep the modal open and surface the warning to the admin.
        if (data?.warning) {
          setWarning(data.warning);
          setSuccessMessage("Invitation saved as pending.");
          setSaving(false);
          // Refresh the parent's list so the new pending row shows up
          // but don't close the modal — admin needs to see the warning.
          return;
        }
        if (data?.message) {
          setSuccessMessage(data.message);
        }
        await onAdded();
      } else {
        throw new Error("Select a user or enter an email to invite.");
      }
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "100%",
          maxWidth: 600,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,.3)",
        }}
      >
        <div style={{padding: "24px 28px 16px", borderBottom: "1px solid #E2E8F0"}}>
          <div style={{fontSize: 12, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, marginBottom: 4}}>
            Add user
          </div>
          <div style={{fontSize: 22, fontWeight: 800, color: "#1B3A5C"}}>{tenantName}</div>
        </div>

        <div style={{padding: 28, flex: 1, overflowY: "auto"}}>
          {error && (
            <div style={{background: "#FEE2E2", color: "#991B1B", padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13}}>
              {error}
            </div>
          )}

          {warning && (
            <div style={{background: "#FFFBEB", color: "#92400E", padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13, border: "1px solid #FCD34D"}}>
              <strong>Heads up:</strong> {warning}
            </div>
          )}

          {successMessage && !warning && (
            <div style={{background: "#DCFCE7", color: "#15803D", padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13}}>
              {successMessage}
            </div>
          )}

          <input
            type="text"
            placeholder="Search by email or name, or paste a new email to invite..."
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedUserId(null); setInviteEmail(null); }}
            autoFocus
            style={{marginBottom: 14}}
          />

          {loading ? (
            <div style={{color: "#94A3B8", padding: 20, textAlign: "center"}}>Loading users...</div>
          ) : (
            <>
              {candidates.length > 0 && (
                <div style={{display: "flex", flexDirection: "column", gap: 6, maxHeight: 240, overflowY: "auto", marginBottom: 12}}>
                  {candidates.map(u => (
                    <div
                      key={u.id}
                      onClick={() => handleSelect(u)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 14px",
                        border: `2px solid ${selectedUserId === u.id ? "#2563EB" : "#E2E8F0"}`,
                        background: selectedUserId === u.id ? "#EFF6FF" : "#fff",
                        borderRadius: 10,
                        cursor: "pointer",
                        transition: "all .12s",
                      }}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: selectedUserId === u.id ? "#2563EB" : "#1B3A5C",
                        color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 700, fontSize: 13, flexShrink: 0,
                      }}>
                        {(u.display_name || u.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div style={{flex: 1, minWidth: 0}}>
                        <div style={{fontWeight: 700, color: "#1B3A5C", fontSize: 14}}>{u.display_name}</div>
                        <div style={{fontSize: 12, color: "#64748B"}}>{u.email}</div>
                      </div>
                      <div style={{fontSize: 11, color: "#94A3B8"}}>{timeAgo(u.last_sign_in_at)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Invite-by-email option — shown when search is an email and no exact match exists */}
              {canInvite && (
                <div
                  onClick={handleInviteEmailMode}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    border: `2px dashed ${inviteEmail ? "#F59E0B" : "#FCD34D"}`,
                    background: inviteEmail ? "#FFFBEB" : "#FEFCE8",
                    borderRadius: 10,
                    cursor: "pointer",
                    transition: "all .12s",
                    marginBottom: 14,
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: inviteEmail ? "#F59E0B" : "#FBBF24",
                    color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16, flexShrink: 0,
                  }}>
                    ✉
                  </div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontWeight: 700, color: "#1B3A5C", fontSize: 14}}>Invite <strong>{trimmedSearch}</strong></div>
                    <div style={{fontSize: 12, color: "#92400E", marginTop: 2}}>
                      They'll be auto-assigned when they first sign in via Authentik
                    </div>
                  </div>
                </div>
              )}

              {/* Empty/no-match state */}
              {candidates.length === 0 && !canInvite && (
                <div style={{
                  background: "#F8FAFC",
                  border: "1px dashed #E2E8F0",
                  borderRadius: 10,
                  padding: 32,
                  textAlign: "center",
                  color: "#64748B",
                  fontSize: 14,
                }}>
                  {alreadyPending
                    ? <>An invitation for <strong>{trimmedSearch}</strong> is already pending.</>
                    : search
                      ? <>No users match "<strong>{search}</strong>".<br/>Type a full email address to send a pending invitation.</>
                      : "Start typing an email or name."}
                </div>
              )}
            </>
          )}

          {(selectedUser || inviteEmail) && (
            <div style={{
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 10,
              padding: 16,
              marginTop: 6,
            }}>
              <div style={{fontSize: 12, color: "#64748B", fontWeight: 600, marginBottom: 6}}>
                Assign role for {selectedUser ? selectedUser.email : inviteEmail}:
              </div>
              <select value={role} onChange={e => setRole(e.target.value)}>
                <option value="admin">Admin — full access within tenant</option>
                <option value="member">Member — standard access</option>
                <option value="viewer">Viewer — read-only</option>
              </select>
            </div>
          )}
        </div>

        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          padding: "16px 28px",
          borderTop: "1px solid #E2E8F0",
          background: "#F8FAFC",
          borderRadius: "0 0 14px 14px",
        }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={(!selectedUserId && !inviteEmail) || saving}
          >
            {saving
              ? (inviteEmail ? "Inviting..." : "Adding...")
              : (inviteEmail ? "Send invitation" : "Add to tenant")}
          </button>
        </div>
      </div>
    </div>
  );
}
